import { useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ArrowRight, CheckCircle2, CornerDownRight, Truck } from 'lucide-react';
import { useAdminOrder } from '@/lib/queries';
import { AdminLayout } from '@/components/AdminLayout';
import { ChannelChip } from '@/components/ChannelChip';
import { SupplierOrderPanel, SupplierStatusBadge } from '@/components/SupplierOrderPanel';
import { StatusBadge, orderStatusTone, humaniseStatus } from '@/components/ui/badge';
import { cn, formatDateTime, formatQuantity } from '@/lib/utils';
import type { AdminOrderVendor } from '@/lib/types';

// Admin order detail: how the order splits into one outgoing PO per supplier,
// and the communication trace with each supplier over its preferred channel.
export function AdminOrderDetail() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, isError } = useAdminOrder(id);
  const order = data?.data;
  const [searchParams] = useSearchParams();
  // ?supplier=<id> (from a dashboard alert) opens that supplier's trace.
  const focusSupplier = searchParams.get('supplier');
  const [selectedId, setSelectedId] = useState<string | null>(focusSupplier);
  const [highlight, setHighlight] = useState(false);
  const traceRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!order || !focusSupplier || !order.vendors.some((v) => v.supplier.id === focusSupplier)) return;
    setSelectedId(focusSupplier);
    setHighlight(true);
    requestAnimationFrame(() => traceRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    const t = setTimeout(() => setHighlight(false), 2500);
    return () => clearTimeout(t);
  }, [order, focusSupplier]);

  const vendors = order?.vendors ?? [];
  const selected = vendors.find((v) => v.supplier.id === selectedId) ?? vendors[0];
  // A rerouted item has two lines (back-ordered + replacement); count it once.
  const itemCount = vendors.reduce((n, v) => n + v.lines.filter((l) => !l.reroutedFromLineId).length, 0);

  // Start each supplier's trace at the top when switching suppliers.
  useEffect(() => {
    traceRef.current?.firstElementChild?.scrollTo({ top: 0 });
  }, [selected?.supplier.id]);

  return (
    <AdminLayout>
      {/* On wide screens the routing section is exactly one viewport tall: the
          page only scrolls past the summary above it, and the routing list and
          trace panel scroll on their own. */}
      <div className="xl:flex xl:flex-col">
        <nav className="mb-5 shrink-0 text-sm text-muted">
          <Link to="/admin/history" className="hover:text-brand">
            Order History
          </Link>
          <span className="mx-2">›</span>
          <span className="font-mono text-brand">{order?.orderNumber ?? '…'}</span>
        </nav>

        {isLoading && <p className="text-muted">Loading order…</p>}
        {isError && <p className="text-danger">Order not found.</p>}

        {order && (
          <>
            <div className="card mb-8 shrink-0 px-8 py-6">
              <div className="flex flex-wrap items-center gap-x-12 gap-y-4">
                <div className="min-w-0">
                  <div className="font-mono text-2xl leading-tight text-brand">{order.orderNumber}</div>
                  <div className="font-head text-base font-extrabold uppercase text-ink">{order.locationName}</div>
                </div>
                <dl className="flex flex-1 flex-wrap gap-x-12 gap-y-3 text-sm">
                  <Meta label="Placed by" value={order.placedBy} />
                  <Meta label="Submitted" value={order.submittedAt ? formatDateTime(order.submittedAt) : 'Draft'} />
                  <Meta label="Items" value={String(itemCount)} />
                  <Meta label="Outgoing POs" value={`${vendors.length} supplier${vendors.length === 1 ? '' : 's'}`} />
                </dl>
                <StatusBadge tone={orderStatusTone(order.status)}>{humaniseStatus(order.status)}</StatusBadge>
              </div>
              {order.notes && (
                <p className="mt-4 border-l-2 border-brand/50 pl-4 text-sm italic text-muted">“{order.notes}”</p>
              )}
            </div>

            <ExpectedDeliveries vendors={vendors} selectedId={selected?.supplier.id} onSelect={setSelectedId} />

            <BackOrders vendors={vendors} />

            <div className="mb-4 shrink-0">
              <h2 className="section-head">Routing</h2>
              <p className="text-sm text-muted">One outgoing purchase order per supplier. Select one to see its trace.</p>
            </div>

            {/* Supplier cards and the trace panel start on the same line. */}
            <div className="grid grid-cols-1 gap-8 xl:h-[calc(100vh-7.5rem)] xl:min-h-[34rem] xl:grid-cols-12">
              <section className="flex min-h-0 flex-col xl:col-span-5">
                <div className="space-y-4 xl:-mr-2 xl:min-h-0 xl:flex-1 xl:overflow-y-auto xl:pb-6 xl:pr-2">
                  {vendors.map((v) => (
                    <VendorCard
                      key={v.supplier.id}
                      vendor={v}
                      selected={v.supplier.id === selected?.supplier.id}
                      onSelect={() => setSelectedId(v.supplier.id)}
                    />
                  ))}
                </div>
              </section>

              {selected && (
                <section className="min-h-0 xl:col-span-7">
                  <div ref={traceRef} className={cn('scroll-mt-6 transition-shadow duration-700 xl:h-full', highlight && 'ring-2 ring-brand')}>
                    <SupplierOrderPanel vendor={selected} className="xl:h-full xl:overflow-y-auto" />
                  </div>
                </section>
              )}
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="eyebrow">{label}</dt>
      <dd className="text-ink">{value}</dd>
    </div>
  );
}

function VendorCard({ vendor, selected, onSelect }: { vendor: AdminOrderVendor; selected: boolean; onSelect: () => void }) {
  const po = vendor.purchaseOrder;
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={cn(
        'card block w-full p-6 text-left transition-colors',
        selected ? 'border-brand ring-1 ring-brand' : 'hover:border-brand/40',
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="font-mono text-sm text-brand">
            {po?.poNumber ?? 'Not created yet'}
            {po?.supplierOrderRef && <span className="text-faint"> · {vendor.supplier.name.split(' ')[0]} #{po.supplierOrderRef}</span>}
          </div>
          <div className="truncate font-head text-lg font-extrabold uppercase text-ink">{vendor.supplier.name}</div>
        </div>
        <div className="flex items-center gap-2">
          <ChannelChip channel={vendor.supplier.dispatchChannel} />
          <SupplierStatusBadge status={vendor.status} />
        </div>
      </div>
      {po?.expectedDeliveryAt && (
        <div className="mt-2 flex items-center gap-1.5 text-xs text-muted">
          <Truck className="h-3.5 w-3.5 text-brand" /> Expected {formatDateTime(po.expectedDeliveryAt)}
        </div>
      )}
      <ul className="mt-4 divide-y divide-line border-t border-line text-sm">
        {vendor.lines.map((l) => (
          <li key={l.id} className="flex items-baseline justify-between gap-3 py-2.5">
            <div className="min-w-0">
              <span className="text-ink">{l.productName}</span>
              {l.reroutedFromSupplierName && (
                <span className="ml-2 inline-flex items-center gap-1 text-xs text-amber">
                  <CornerDownRight className="h-3 w-3" /> rerouted from {l.reroutedFromSupplierName}
                </span>
              )}
              {['back_ordered', 'rerouted'].includes(l.status) && !l.reroutedFromSupplierName && (
                <span className="ml-2 text-xs text-amber">{humaniseStatus(l.status)}</span>
              )}
              <div className="font-mono text-xs text-faint">{l.supplierSku}</div>
            </div>
            <span className="whitespace-nowrap text-muted">
              {formatQuantity(l.quantity)} {l.unitOfMeasure}
            </span>
          </li>
        ))}
      </ul>
    </button>
  );
}

// Calendar-day distance from today, as people say it.
function relativeDay(d: Date): string {
  const start = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((start(d) - start(new Date())) / 86_400_000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff === -1) return 'Yesterday';
  return diff > 0 ? `In ${diff} days` : `${-diff} days ago`;
}

// One card per supplier, in delivery order. Past deliveries are greyed out;
// upcoming ones stand out, and the next one is flagged.
function ExpectedDeliveries({
  vendors,
  selectedId,
  onSelect,
}: {
  vendors: AdminOrderVendor[];
  selectedId: string | undefined;
  onSelect: (supplierId: string) => void;
}) {
  const etaOf = (v: AdminOrderVendor) =>
    v.purchaseOrder?.expectedDeliveryAt ? Date.parse(v.purchaseOrder.expectedDeliveryAt) : Infinity;
  const sorted = [...vendors].sort((a, b) => etaOf(a) - etaOf(b));
  const now = Date.now();
  const nextId = sorted.find((v) => etaOf(v) !== Infinity && etaOf(v) >= now)?.supplier.id;

  return (
    <section className="mb-8 shrink-0">
      <h2 className="section-head mb-3">Expected deliveries</h2>
      <ol className="-mx-1 flex gap-4 overflow-x-auto px-1 pb-3 pt-1">
        {sorted.map((v) => {
          const po = v.purchaseOrder;
          const eta = po?.expectedDeliveryAt ? new Date(po.expectedDeliveryAt) : null;
          const past = eta !== null && eta.getTime() < now;
          const isNext = v.supplier.id === nextId;
          const selected = v.supplier.id === selectedId;
          return (
            <li key={v.supplier.id} className="w-60 shrink-0">
              <button
                type="button"
                onClick={() => onSelect(v.supplier.id)}
                className={cn(
                  'relative flex h-full w-full flex-col gap-1.5 overflow-hidden rounded-card border p-5 text-left transition-all duration-200',
                  !eta && 'border-dashed border-line bg-surface text-muted',
                  eta && past && 'border-line bg-surface opacity-50 grayscale hover:opacity-80',
                  eta &&
                    !past &&
                    'border-brand/40 bg-gradient-to-br from-brand/20 via-surface-2 to-surface shadow-[0_10px_30px_-12px_rgba(0,171,212,0.55)] hover:-translate-y-0.5 hover:shadow-[0_14px_34px_-10px_rgba(0,171,212,0.7)]',
                  isNext && 'border-brand shadow-[0_12px_36px_-10px_rgba(0,171,212,0.8)]',
                  selected && 'ring-2 ring-brand ring-offset-2 ring-offset-canvas',
                )}
              >
                <div className="flex items-center justify-between gap-2">
                  <span
                    className={cn(
                      'text-[11px] font-bold uppercase tracking-[0.14em]',
                      past ? 'text-muted' : eta ? 'text-brand' : 'text-faint',
                    )}
                  >
                    {eta ? relativeDay(eta) : 'Not scheduled'}
                  </span>
                  {isNext ? (
                    <span className="rounded-full bg-brand px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#04181d]">
                      Next
                    </span>
                  ) : past ? (
                    <span className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-muted">
                      <CheckCircle2 className="h-3.5 w-3.5" /> Delivered
                    </span>
                  ) : (
                    <Truck className="h-4 w-4 text-brand" />
                  )}
                </div>
                <div className={cn('font-head text-3xl font-extrabold leading-none', past ? 'text-muted' : 'text-ink')}>
                  {eta ? eta.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }) : '—'}
                </div>
                <div className="text-xs text-muted">
                  {eta ? eta.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' }) : 'No delivery date yet'}
                </div>
                <div className={cn('mt-3 border-t pt-3', past ? 'border-line' : 'border-brand/20')}>
                  <div className={cn('truncate text-sm font-semibold', past ? 'text-muted' : 'text-ink')}>{v.supplier.name}</div>
                  <div className="mt-0.5 truncate font-mono text-xs text-brand/80">
                    {po?.poNumber ?? '—'}
                    {po?.supplierOrderRef && <span className="text-faint"> · #{po.supplierOrderRef}</span>}
                  </div>
                </div>
                {!past && eta && (
                  <div className="mt-2">
                    <SupplierStatusBadge status={v.status} />
                  </div>
                )}
              </button>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

// One line per back-ordered item: which supplier was out, and where it was
// reordered from instead (or that nobody is left to order from).
function BackOrders({ vendors }: { vendors: AdminOrderVendor[] }) {
  const lines = vendors.flatMap((v) => v.lines.map((l) => ({ ...l, supplierName: v.supplier.name })));
  const backOrdered = lines.filter((l) => l.status === 'back_ordered');
  if (backOrdered.length === 0) return null;

  return (
    <section className="mb-8 shrink-0">
      <h2 className="section-head mb-3">Back-orders</h2>
      <ul className="card max-h-28 divide-y divide-line overflow-y-auto">
        {backOrdered.map((bo) => {
          const replacement = lines.find((l) => l.reroutedFromLineId === bo.id);
          return (
            <li key={bo.id} className="flex flex-wrap items-center gap-x-2 gap-y-1 px-6 py-3.5 text-sm">
              <span className="font-medium text-ink">{bo.productName}</span>
              <span className="font-mono text-xs text-faint">({bo.supplierSku})</span>
              <span className="text-muted">from</span>
              <span className="text-ink">{bo.supplierName}</span>
              <span className="text-amber">was back-ordered</span>
              {replacement ? (
                <>
                  <ArrowRight className="h-4 w-4 text-faint" />
                  <span className="text-muted">ordering from</span>
                  <span className="font-medium text-ink">{replacement.supplierName}</span>
                  <span className="font-mono text-xs text-faint">({replacement.supplierSku})</span>
                </>
              ) : (
                <span className="text-danger">— no fallback supplier left, source it manually</span>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
